import Alert from '@/components/ui/alert';
import CountdownTimer from '@/components/ui/countdown-timer';
import Logo from '@/components/ui/logo';
import { useSettings } from '@/framework/settings';
import { useHeaderSearch } from '@/layouts/headers/header-search-atom';
import {
  RESPONSIVE_WIDTH,
  checkIsMaintenanceModeComing,
  checkIsMaintenanceModeStart,
  checkIsScrollingStart,
  checkIsShopMaintenanceModeComing,
  checkIsShopMaintenanceModeStart,
  isMultiLangEnable,
} from '@/lib/constants';
import { useActiveScroll } from '@/lib/use-active-scroll';
import { useIsHomePage } from '@/lib/use-is-homepage';
import { authorizationAtom } from '@/store/authorization-atom';
import { drawerAtom } from '@/store/drawer-atom';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useShop, useShopMaintenanceEvent } from '@/framework/shop';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import WalletButton from './menu/wallet-button';
import ShopButton from './menu/shop-button';
import { useWindowSize } from '@/lib/hooks/use-window-size';
import CategoriesRow from './categories-row';
import { NavbarIcon } from '../icons/navbar-icon';
import { BarcodeIcon } from '../icons/barcode-icon';
import CategoriesRowMobile from './categories-row-mobile';
import BarcodeScanner, {
  preloadBarcodeScanner,
} from '../ui/search/barcode-scanner-lazy';
import { useScannerNativeBridge } from '@/lib/hooks/use-scanner-native-bridge';
import { preloadManagedDrawer } from '@/components/ui/drawer/lazy-managed-drawer';
import { MAIN_SEARCH_INDEX_ID } from '@/lib/services/typesense/constants';
import { ExpressBuyWithPrescriptionCta } from '@/components/prescription-analysis/express-buy-with-prescription-cta';
const SearchWithSuggestion = dynamic(
  () => import('../ui/search/search-with-suggestion-typesense'),
  {
    ssr: false,
  },
);

const AuthorizedMenu = dynamic(() => import('./menu/authorized-menu'), {
  ssr: false,
});
const JoinButton = dynamic(() => import('./menu/join-button'), { ssr: false });

const HEADER_BUTTON_CLASS_NAME = 'font-semibold !bg-transparent';

const Header = ({ layout }: { layout?: string }) => {
const { show } = useHeaderSearch();
  const router = useRouter();
  const {
    query: { slug },
    pathname,
  } = router;
  // Only fetch shop on shop pages (/shops/[slug]); on product pages ([slug]/product/[sku]) slug is the product slug
  const isShopPage = pathname?.startsWith('/shops');
  const { data: shopData, isLoading } = useShop({
    slug: slug as string,
    enabled: Boolean(slug) && Boolean(isShopPage),
  });
  const { createShopMaintenanceEventRequest } = useShopMaintenanceEvent();
  const [_, setDrawerView] = useAtom(drawerAtom);
  const [isAuthorize] = useAtom(authorizationAtom);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMounted, setScannerMounted] = useState(false);
  const isHomePage = useIsHomePage();
  const siteHeaderRef = React.useRef(null);
  useActiveScroll(siteHeaderRef);
  useScannerNativeBridge();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const run = () => {
      preloadBarcodeScanner();
      preloadManagedDrawer();
    };
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 5000 });
    } else {
      timeoutId = setTimeout(run, 3000);
    }
    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  const isFlattenHeader = useMemo(
    () => !show && isHomePage && layout !== 'modern',
    [show, isHomePage, layout],
  );

  const handleSidebar = useCallback((view: string) => {
    return setDrawerView({ display: true, view });
  }, [setDrawerView]);

  const preloadDrawerAndScanner = useCallback(() => {
    preloadManagedDrawer();
    preloadBarcodeScanner();
  }, []);

  const closeLocation = () => setOpenDropdown(false);
  const { settings } = useSettings();
  const [underMaintenanceIsComing] = useAtom(checkIsMaintenanceModeComing);
  const [shopUnderMaintenanceIsComing] = useAtom(
    checkIsShopMaintenanceModeComing,
  );
  const [__, setUnderMaintenanceStart] = useAtom(checkIsMaintenanceModeStart);
  const [___, setShopUnderMaintenanceStart] = useAtom(
    checkIsShopMaintenanceModeStart,
  );
  const [isScrolling] = useAtom(checkIsScrollingStart);
  const { width } = useWindowSize();


  const allowedRoutes = [
    '/search',
    '/search/[[...category]]',
    '/[slug]/product/[sku]',
  ];

  function onBarcodeSearch(term: string) {
    const keyName = `${MAIN_SEARCH_INDEX_ID}[query]`;

    const url = `/search?${encodeURIComponent(keyName)}=${encodeURIComponent(term)}`;

    router.push(url);
  }

  const openScanner = () => {
    if (window.QualiAppNative?.openScanner) {
      window.QualiAppNative.openScanner();
      return;
    }
    setScannerMounted(true);
    setShowScanner(true);
  };

  return (
      <header
        id="site-header"
        ref={siteHeaderRef}
        className={twMerge(
          cn(
            'site-header-with-search bg-color-primary top-0 z-50 w-full transition-all',
            {
              '': isFlattenHeader,
              sticky: isHomePage,
              'sticky border-b border-border-200 shadow-sm': !isHomePage,
              'lg:pt-2': !underMaintenanceIsComing,
              'lg:h-auto': shopUnderMaintenanceIsComing,
            },
          ),
        )}
      >
        {width >= RESPONSIVE_WIDTH &&
        underMaintenanceIsComing &&
        !isScrolling &&
        !shopUnderMaintenanceIsComing ? (
          <Alert
            message={`Site ${"Modo de Manutenção Começa em"}`}
            variant="info"
            className="sticky top-0 left-0 z-50"
            childClassName="flex justify-center items-center w-full gap-4"
          >
            <CountdownTimer
              date={new Date(settings?.maintenance?.start as string)}
              className="text-blue-600 [&>p]:bg-blue-200 [&>p]:p-2 [&>p]:text-xs [&>p]:text-blue-600"
              onComplete={() => setUnderMaintenanceStart(true)}
            />
          </Alert>
        ) : (
          ''
        )}
        {width >= RESPONSIVE_WIDTH &&
        !underMaintenanceIsComing &&
        !isScrolling &&
        shopUnderMaintenanceIsComing &&
        !isLoading &&
        shopData ? (
          <Alert
            message={`${shopData?.name} ${"Modo de Manutenção Começa em"}`}
            variant="info"
            className="sticky top-0 left-0 z-50"
            childClassName="flex justify-center items-center font-bold w-full gap-4"
          >
            <CountdownTimer
              date={
                new Date(shopData?.settings?.shopMaintenance?.start as string)
              }
              className="text-blue-600 [&>p]:bg-blue-200 [&>p]:p-2 [&>p]:text-xs [&>p]:text-blue-600"
              onComplete={() => {
                setShopUnderMaintenanceStart(true);
                createShopMaintenanceEventRequest({
                  shop_id: shopData?.id,
                  isMaintenance: true,
                  isShopUnderMaintenance: Boolean(
                    shopData?.settings?.isShopUnderMaintenance,
                  ),
                });
              }}
            />
          </Alert>
        ) : (
          ''
        )}
        <button
          type="button"
          aria-label="Fechar seleção de localização"
          className={cn(
            'fixed inset-0 -z-10 h-[100vh] w-full bg-black/50',
            openDropdown === true ? '' : 'hidden',
          )}
          onClick={closeLocation}
        />
        <div className="lg:flex lg:justify-center">
          <div className="w-full max-w-[1560px] mx-auto lg:px-6 2xl:px-8">
            <div
              className={cn(
                'flex w-full transform-gpu items-center justify-between px-5 pt-4 pb-2 transition-transform duration-300 gap-x-4 lg:py-0',
                {
                  'lg:border-0 lg:bg-transparent lg:shadow-none':
                    isFlattenHeader,
                  'lg:!bg-light': openDropdown,
                },
              )}
            >
              {/* Commented menu button for medium size screens */}
              {/* <motion.button
                onClick={() => handleSidebar('MAIN_MENU_VIEW')}
                className="group hidden h-full w-6 shrink-0 items-center justify-center focus:text-accent focus:outline-0 ltr:mr-6 rtl:ml-6 lg:flex xl:hidden"
              >
                <span className="sr-only">{"Menu de Hambúrguer"}</span>
                <div className="flex w-full flex-col space-y-1.5">
                  <span className="h-0.5 w-full rounded bg-white transition-all group-hover:w-full" />
                  <span className="h-0.5 w-full rounded bg-white transition-all group-hover:w-3/4" />
                  <span className="h-0.5 w-full rounded bg-white transition-all group-hover:w-full" />
                </div>
              </motion.button> */}

              {/* Mobile View */}
              <div className="flex w-full items-center justify-between lg:hidden">
                <motion.button
                  whileTap={{ scale: 0.88 }}
                  onClick={() => handleSidebar('MAIN_MENU_VIEW')}
                  onPointerEnter={preloadDrawerAndScanner}
                  onFocus={preloadDrawerAndScanner}
                  onTouchStart={preloadDrawerAndScanner}
                  className="h-full focus:text-accent focus:outline-0 bg-dark-blue px-2 py-3 rounded-lg"
                >
                  <span className="sr-only">{"Menu de Hambúrguer"}</span>
                  <NavbarIcon className="text-white h-5 mx-auto" />
                </motion.button>

                <div className="flex-1 px-4">
                  <SearchWithSuggestion
                    label={"pesquisa de mantimentos no cabeçalho"}
                    variant="minimal"
                    enableTypesenseAnalytics
                  />
                </div>

                <motion.button
                  whileTap={{ scale: 0.88 }}
                  className="h-full focus:text-accent focus:outline-0"
                  onClick={openScanner}
                  onPointerEnter={preloadBarcodeScanner}
                  onFocus={preloadBarcodeScanner}
                  onTouchStart={preloadBarcodeScanner}
                >
                  <span className="sr-only">Leitor de código de barras</span>
                  <BarcodeIcon className="h-7 w-7" />
                </motion.button>

                {scannerMounted ? (
                  <BarcodeScanner
                    onScan={(code) => {
                      onBarcodeSearch(code);
                      setShowScanner(false);
                    }}
                    onClose={() => setShowScanner(false)}
                    isOpen={showScanner}
                  />
                ) : null}
              </div>

              {/* Desktop View */}
              <div className="hidden w-full items-center justify-between lg:flex">
                {/* Left: Logo */}
                <div className="flex items-center gap-x-6">
                  <Logo
                    className={cn(
                      'py-3 max-w-36',
                      !isMultiLangEnable
                        ? 'mx-auto lg:mx-0'
                        : 'ltr:ml-0 rtl:mr-0 ',
                    )}
                    useWhite
                  />
                </div>

                {/* Center: Search */}
                <div className="flex h-full flex-1 items-center justify-center px-8 py-1.5 border-b-accent-300 bg-light backdrop-blur ltr:left-0 rtl:right-0 lg:bg-color-primary lg:border-none">
                  <SearchWithSuggestion
                    label={"pesquisa de mantimentos no cabeçalho"}
                    variant="minimal"
                    enableTypesenseAnalytics
                  />
                </div>

                <ExpressBuyWithPrescriptionCta
                  variant="header"
                  className="mx-2"
                />

                {/* Right: BarcodeIcon and any other buttons */}
                <div className="flex items-center gap-x-8 px-3">
                  {isAuthorize ? (
                    <AuthorizedMenu />
                  ) : (
                    <JoinButton className={HEADER_BUTTON_CLASS_NAME} />
                  )}
                  {/* TODO: uncomment when page is ready */}
                  {/* <OffersButton /> */}
                  <WalletButton className={HEADER_BUTTON_CLASS_NAME} />
                  <ShopButton className={HEADER_BUTTON_CLASS_NAME} />
                </div>
              </div>
            </div>

            <div className="flex lg:hidden">
              <CategoriesRowMobile />
            </div>

            <div className="hidden lg:block">
              <CategoriesRow isAuthorize={isAuthorize} />
            </div>
          </div>
        </div>
      </header>
  );
};

export default Header;
